import React from 'react'
import { Box, Divider, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Breadcrumbs from '@hapana/shared/web/components/shared/breadcrumbs/breadcrumbs.component'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import MobileBreadCrumbs from '@hapana/shared/web/components/shared/breadcrumbs/mobile-breadcrumbs/mobile-breadcrumbs.component'
import { useCorporateWithStore } from '_hooks/use-corporate.hook'
import { useMe } from '_hooks/me.hooks'
import useSiteStore from '_stores/site.store'
import HapanaSparkIcon from '@hapana/shared/web/styles/assets/images/hapana-spark-icon.png'
import LocationSwitcher from '../location-switcher/location-switcher.component'

import SupportLauncher from '../support-assistant/components/support-launcher'
import { CORE_PACKAGE, formatBreadcrumbLabel } from './breadcrumbs.constant'
import { styles } from './breadcrumbs.styles'

const DashboardWelcomeBreadcrumb = ({ entityName, userName, hasSiteContext }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const p = theme.palette

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        px: 3,
        minHeight: { xs: 'auto', sm: 64 },
        py: { xs: 2, sm: 0 },
        justifyContent: { sm: 'center' },
      }}
    >
      {/* Desktop (sm+): horizontal row layout with the inline location pill switcher */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src={HapanaSparkIcon}
          alt="Hapana"
          sx={{ width: 28, height: 28, objectFit: 'contain' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              fontSize: 16,
              color: p.text.primary,
              lineHeight: 1.3,
            }}
          >
            {t('core:locationSwitch.welcomeTo', 'Welcome to')} {entityName},{' '}
            <Typography
              component="span"
              sx={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 400,
                fontSize: 16,
                color: p.text.secondary,
              }}
            >
              {userName}
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: p.text.secondary,
                lineHeight: 1.3,
              }}
            >
              {hasSiteContext
                ? t('core:locationSwitch.locationDashboard', 'Your location dashboard')
                : t('core:locationSwitch.selectOrManage', 'Select a location or manage your brand')}
            </Typography>
            <LocationSwitcher variant="pill" />
          </Box>
        </Box>
        <SupportLauncher inline />
      </Box>

      {/* Mobile: stacked layout */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0.5 }}>
        <Typography
          sx={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 400,
            fontSize: 18,
            color: p.text.secondary,
            lineHeight: 1.3,
          }}
        >
          {t('core:locationSwitch.welcomeTo', 'Welcome to')}{' '}
          <Typography
            component="span"
            sx={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              fontSize: 18,
              color: p.text.primary,
            }}
          >
            {entityName},
          </Typography>
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 700,
            fontSize: 22,
            color: p.text.primary,
            lineHeight: 1.2,
          }}
        >
          {userName}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: p.text.secondary,
            lineHeight: 1.3,
            mt: 0.25,
          }}
        >
          {hasSiteContext
            ? t('core:locationSwitch.locationDashboard', 'Your location dashboard')
            : t('core:locationSwitch.selectOrManage', 'Select a location or manage your brand')}
        </Typography>

        {/* Full-width pill location switcher */}
        <Box sx={{ mt: 1.5 }}>
          <LocationSwitcher variant="pill" data-testid="location-switcher-mobile" />
        </Box>
      </Box>
    </Box>
  )
}

const CoreBreadCrumbs = ({ breadcrumbs, pathname, tabs, params, entityType }) => {
  const isMobile = useMediaQuery(
    (theme) => theme?.breakpoints?.down('sm') || '@media (max-width: 600px)'
  )
  const { brandName, isLoading: loadingCorporateByPrefix } = useCorporateWithStore()
  const currentSite = useSiteStore((state) => state.currentSite)
  const { data: meData } = useMe()

  const isLoadingEntity = loadingCorporateByPrefix

  const entityName = currentSite?.name || brandName

  const userName = meData?.me
    ? `${meData.me.firstName ?? ''} ${meData.me.lastName ?? ''}`.trim()
    : ''

  // Detect dashboard/home route: / (root), /c/ (brand), or /s/{siteId}/ (location)
  const isDashboard =
    pathname === '/' || pathname === '/c/' || pathname === '/c' || /^\/s\/[^/]+\/?$/.test(pathname)

  const hasSiteContext = Boolean(currentSite?.siteId)

  const formattedBreadcrumbs = breadcrumbs.map((item) => ({
    ...item,
    label: formatBreadcrumbLabel(item.label, false),
  }))

  return (
    <Box sx={styles.emptyRootContent}>
      <Box sx={styles.breadcrumbsWrapper}>
        {isDashboard ? (
          <DashboardWelcomeBreadcrumb
            entityName={entityName || entityType}
            userName={userName}
            hasSiteContext={hasSiteContext}
          />
        ) : !isMobile ? (
          <Box sx={styles.breadcrumbs}>
            {isLoadingEntity ? (
              <Loading />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                <Breadcrumbs
                  breadcrumbs={formattedBreadcrumbs}
                  hasRouteIntegration
                  packageType={CORE_PACKAGE}
                  entityName={entityName || entityType}
                  pathname={pathname}
                  tabs={tabs}
                  params={params}
                />
                <LocationSwitcher variant="pill" />
                <SupportLauncher inline />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={styles.breadcrumbs}>
            <MobileBreadCrumbs entityName={entityName || entityType} />
          </Box>
        )}
      </Box>

      {!isMobile && <Divider sx={styles.dividerStyling} />}
    </Box>
  )
}

CoreBreadCrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      href: PropTypes.string.isRequired,
    })
  ).isRequired,
  pathname: PropTypes.string,
  params: PropTypes.object,
  entityType: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  impersonationInfo: PropTypes.shape({
    adminUser: PropTypes.object,
    staffUser: PropTypes.object,
  }),
}

export default CoreBreadCrumbs
